import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  ApiError,
  getHealth,
  OfflineError,
  withOfflineDetection,
} from '@platform/client';
import { useCallback, useState } from 'react';

import { useAuth } from '../auth/AuthProvider';
import { env } from '../env';
import { TelemetryProbe } from '../TelemetryProbe';

import { PageHeader, SCREEN_GAP } from './PageHeader';
import { WhoAmI } from './WhoAmI';

type Result =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; status: string; release: string; uptime: number }
  | { kind: 'error'; message: string; code: string; correlationId: string };

/**
 * The health probe, and what it proves: the admin reaches the API through generated,
 * typed code, and a failure arrives as a typed `ApiError` carrying a correlation id
 * rather than as a string.
 *
 * This was `AppShell` until `NAV-01`, when the shell became a layout with a drawer. The
 * screen is unchanged — only the chrome moved out from underneath it.
 */
export function Diagnostics() {
  const [result, setResult] = useState<Result>({ kind: 'idle' });
  const auth = useAuth();

  const signOut = useCallback(() => {
    void auth.signOut();
  }, [auth]);

  const callApi = useCallback(() => {
    setResult({ kind: 'loading' });

    void (async () => {
      try {
        const response = await withOfflineDetection(() => getHealth());
        const data = response.data;
        setResult({
          kind: 'ok',
          status: data?.status ?? 'unknown',
          release: data?.release ?? 'unknown',
          uptime: data?.uptime ?? 0,
        });
      } catch (error) {
        if (error instanceof ApiError) {
          setResult({
            kind: 'error',
            message: error.message,
            code: error.code,
            correlationId: error.correlationId,
          });
          return;
        }
        if (error instanceof OfflineError) {
          setResult({
            kind: 'error',
            message: 'Could not reach the API. Is it running?',
            code: 'OFFLINE',
            correlationId: error.correlationId,
          });
          return;
        }
        throw error;
      }
    })();
  }, []);

  return (
    <Stack spacing={SCREEN_GAP} sx={{ maxWidth: 720 }}>
      <PageHeader
        title="Diagnostics"
        subtitle={`Talking to ${env.VITE_API_URL}`}
        action={
          // alignItems goes through sx: MUI v9's Stack dropped it as a direct prop,
          // and the overload error it raises does not name it.
          <Stack spacing={1} sx={{ alignItems: 'flex-end' }}>
            <Typography variant="body2" color="text.secondary">
              {auth.session?.user.email}
            </Typography>
            <Button onClick={signOut} size="small">
              Sign out
            </Button>
          </Stack>
        }
      />

      <WhoAmI />

      {/* Hidden for a non-admin, and that is a courtesy rather than a control: the
          API answers 403 to the same account whatever this renders. Spec 004 §4. */}
      {auth.isAdmin ? (
        <Alert severity="info">Signed in as an administrator.</Alert>
      ) : (
        <Alert severity="warning">
          This account is not an administrator. Admin routes will refuse it.
        </Alert>
      )}

      {/* `color="primary"` resolves to color.accent[500] through the theme. The
          brand travels from @platform/tokens into MUI, not around it. */}
      <Button
        variant="contained"
        color="primary"
        onClick={callApi}
        disabled={result.kind === 'loading'}
        sx={{ alignSelf: 'flex-start' }}
      >
        {result.kind === 'loading' ? 'Calling…' : 'Call the API'}
      </Button>

      <Stack spacing={2}>
        {result.kind === 'idle' && (
          <Typography variant="body2" color="text.secondary">
            Not called yet.
          </Typography>
        )}
        {result.kind === 'ok' && (
          <Alert severity="success">
            <Typography component="pre" variant="body2" sx={PANEL_TEXT}>
              {`status:  ${result.status}
release: ${result.release}
uptime:  ${result.uptime.toFixed(2)}s`}
            </Typography>
          </Alert>
        )}
        {result.kind === 'error' && (
          <Alert severity="error">
            <Typography component="pre" variant="body2" sx={PANEL_TEXT}>
              {`code:           ${result.code}
message:        ${result.message}
correlation id: ${result.correlationId}`}
            </Typography>
          </Alert>
        )}
      </Stack>

      <TelemetryProbe />
    </Stack>
  );
}

const PANEL_TEXT = { m: 0, fontFamily: 'monospace' } as const;
